import { Box, Button, Container } from '@mui/material'
import { useOperationsStore } from '@operations/AppProvider'
import { StoppageReasonsTable } from '@operations/features/stoppageReason/components/organisms/StoppageReasonTable'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog, Header, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch, withAuthenticationRequired } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router'

export const StoppageReason = withAuthenticationRequired(
  observer(() => {
    const theme = useMinimalsTheme()
    const { t } = useTranslate()
    const navigate = useNavigate()
    const title = t('stoppageReason', 'Stoppage reason')
    const breadcrumbs = [
      { label: t(PATHS.masterData.title), route: PATHS.masterData.url },
      {
        label: title,
      },
    ]

    const { stoppageReasonStore, stoppageReasonViewStore, appStore } = useOperationsStore()

    useAsyncFetch(async () => {
      await stoppageReasonStore.fetchStoppageReasons()
    }, [stoppageReasonStore])

    const toggleDeleteDialog = (id?: number) => {
      stoppageReasonViewStore.setDeleteOpen(!stoppageReasonViewStore.isDeleteOpen, id)
    }

    const handleDelete = async () => {
      if (!stoppageReasonViewStore.itemId) return
      try {
        await stoppageReasonStore.onDelete(stoppageReasonViewStore.itemId)
        toggleDeleteDialog()
        appStore.setShowAlert(
          'success',
          t('stoppageReasonDeletedSuccessfully', 'Stoppage reason deleted successfully'),
        )
      } catch (e) {
        appStore.setShowAlert(
          'error',
          t('stoppageReasonDeletionFailed', 'Stoppage reason deletion failed'),
        )
      }
    }

    return (
      <>
        <Helmet>
          <title>{t('stoppageReason', 'Stoppage reason - Tom Operation')}</title>
        </Helmet>

        <Box
          sx={{
            bgcolor: theme.palette.grey[100],
            minHeight: '100%',
          }}
        >
          <Header
            title={title}
            position='relative'
            breadcrumbs={breadcrumbs}
            rightRenderOption={
              <>
                <Button
                  sx={{ flex: 'none' }}
                  variant='contained'
                  onClick={() => navigate(`${PATHS.masterData.stoppageReason.url}/new`)}
                  data-cy='add-new-stoppage-reason-btn'
                >
                  {t('createStoppageReason', 'Create stoppage reason')}
                </Button>
              </>
            }
          />
          <Container maxWidth='lg' sx={{ padding: theme.customSpacing.m }}>
            <StoppageReasonsTable
              filteredItems={stoppageReasonViewStore.filteredItems}
              toggleDelete={toggleDeleteDialog}
            />
            <ConfirmationDialog
              open={stoppageReasonViewStore.isDeleteOpen}
              title={t('deleteStoppage', 'Delete stoppage reason')}
              message={t(
                'entityWillBeDeleted',
                '{name} will be deleted and you cannot undo this action!',
                {
                  name:
                    stoppageReasonViewStore.dataToBeEdited?.name ??
                    t('stoppageReason', 'Stoppage reason'),
                },
              )}
              primaryActionText={t('delete', 'Delete')}
              closeLabel={t('cancel', 'Cancel')}
              maxWidth='xs'
              blockClickOutside
              onConfirm={handleDelete}
              onClose={() => toggleDeleteDialog()}
            />
          </Container>
        </Box>
      </>
    )
  }),
)
